import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Row, Spinner, Table } from "react-bootstrap";
import { FaExchangeAlt, FaHistory, FaProjectDiagram, FaSyncAlt } from "react-icons/fa";
import { obtenerAsientosPorEmpresa, sincronizarAsientosPlanilla } from "../../../services/contabilidad/asientosService";
import { formatCurrency, toNum } from "../utils";

const BTN = { background: "linear-gradient(135deg, #3a3a3a 0%, #606060 100%)", border: "none", color: "white" };

const IntegracionPlanillasContable = ({ usuario }) => {
  const rucEmpresa = usuario?.ruc || "";
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [asientos, setAsientos] = useState([]);

  const cargar = useCallback(async () => {
    if (!rucEmpresa) return;
    try { setLoading(true); setAsientos(await obtenerAsientosPorEmpresa(rucEmpresa)); }
    catch (e) { setError("No se pudieron cargar los asientos contables."); }
    finally { setLoading(false); }
  }, [rucEmpresa]);

  useEffect(() => { cargar(); }, [cargar]);

  const ejecutarSync = async () => {
    try {
      setSyncing(true); setError("");
      const synced = await sincronizarAsientosPlanilla(rucEmpresa);
      setSuccess(`Sincronización completada: ${synced} asientos migrados.`); await cargar();
    } catch (e) { setError("No se pudo sincronizar planillas con contabilidad."); }
    finally { setSyncing(false); }
  };

  const resumen = useMemo(() => {
    const p = asientos.filter(a => a.origen === "PLANILLA");
    const totalDebe = p.reduce((acc, x) => acc + toNum(x.total_debe), 0);
    const totalHaber = p.reduce((acc, x) => acc + toNum(x.total_haber), 0);
    return { total: p.length, totalDebe, totalHaber, diferencia: Math.abs(totalDebe - totalHaber) };
  }, [asientos]);

  if (!rucEmpresa) return <Alert variant="warning" className="border-0 shadow-sm">No se encontró RUC de empresa para integrar planillas.</Alert>;

  return (
    <div>
      {error && <Alert variant="danger" onClose={() => setError("")} dismissible className="border-0 shadow-sm">{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess("")} dismissible className="border-0 shadow-sm">{success}</Alert>}

      {/* Barra de acción */}
      <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
        <Card.Body className="p-4">
          <Row className="align-items-center">
            <Col md={1} className="text-center">
              <div className="rounded-3 p-3 d-inline-block" style={BTN}><FaProjectDiagram size={20} /></div>
            </Col>
            <Col md={7}>
              <h5 className="mb-1 fw-semibold">Integración Automática de Nóminas</h5>
              <p className="text-muted mb-0 small">Al aprobar una planilla, el sistema genera automáticamente asientos de naturaleza (62) y destino (94/95).</p>
            </Col>
            <Col md={4} className="text-md-end mt-3 mt-md-0">
              <Button className="rounded-pill px-4 border-0 shadow-sm" style={BTN} onClick={ejecutarSync} disabled={syncing || loading}>
                {syncing ? <><Spinner size="sm" animation="border" className="me-2" />Procesando...</> : <><FaSyncAlt className="me-2" />Sincronización Manual</>}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* KPI cards */}
      <Row className="mb-4 g-3">
        <Col md={3}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
            <Card.Body className="text-center py-4" style={{ background: "linear-gradient(135deg, #3a3a3a 0%, #606060 100%)", color: "white", borderRadius: "15px" }}>
              <FaHistory size="2rem" className="mb-2" />
              <h4 className="fw-bold mb-0">{resumen.total}</h4>
              <p className="mb-0 small opacity-75">Asientos nómina</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
            <Card.Body className="text-center py-4" style={{ background: "linear-gradient(135deg, #059669 0%, #10b981 100%)", color: "white", borderRadius: "15px" }}>
              <FaExchangeAlt size="2rem" className="mb-2" />
              <h4 className="fw-bold mb-0">{formatCurrency(resumen.totalDebe)}</h4>
              <p className="mb-0 small opacity-75">Total Debe</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
            <Card.Body className="text-center py-4" style={{ background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)", color: "white", borderRadius: "15px" }}>
              <FaExchangeAlt size="2rem" className="mb-2" />
              <h4 className="fw-bold mb-0">{formatCurrency(resumen.totalHaber)}</h4>
              <p className="mb-0 small opacity-75">Total Haber</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
            <Card.Body className="text-center py-4" style={{ background: resumen.diferencia === 0 ? "linear-gradient(135deg, #059669 0%, #10b981 100%)" : "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)", color: "white", borderRadius: "15px" }}>
              <FaExchangeAlt size="2rem" className="mb-2" />
              <h4 className="fw-bold mb-0">{formatCurrency(resumen.diferencia)}</h4>
              <p className="mb-0 small opacity-75">Diferencia</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tabla de asientos */}
      <Card className="border-0 shadow-sm" style={{ borderRadius: "15px", overflow: "hidden" }}>
        <div className="table-responsive" style={{ maxHeight: 400 }}>
          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="secondary" /><p className="mt-3 text-muted">Cargando asientos...</p></div>
          ) : (
            <Table hover className="align-middle bg-white mb-0" style={{ fontSize: "0.95rem" }}>
              <thead style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #dee2e6" }}>
                <tr>
                  <th className="py-3 px-4 text-secondary fw-semibold">Fecha</th>
                  <th className="py-3 px-4 text-secondary fw-semibold">Glosa</th>
                  <th className="py-3 px-4 text-secondary fw-semibold text-end">Debe</th>
                  <th className="py-3 px-4 text-secondary fw-semibold text-end">Haber</th>
                  <th className="py-3 px-4 text-secondary fw-semibold text-center">Estado</th>
                </tr>
              </thead>
              <tbody>
                {asientos.filter(a => a.origen === "PLANILLA").length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-5 text-muted">No se registran asientos de planilla migrados.</td></tr>
                ) : asientos.filter(a => a.origen === "PLANILLA").slice(0, 50).map((a) => (
                  <tr key={a.id} style={{ borderBottom: "1px solid #f1f3f4" }}>
                    <td className="px-4 py-3 fw-medium text-dark">{a.fecha || "—"}</td>
                    <td className="px-4 py-3 small" style={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.glosa || "—"}</td>
                    <td className="px-4 py-3 text-end fw-bold">{formatCurrency(a.total_debe)}</td>
                    <td className="px-4 py-3 text-end fw-bold">{formatCurrency(a.total_haber)}</td>
                    <td className="px-4 py-3 text-center">
                      <Badge pill bg={a.estado === "REGISTRADO" ? "success" : "secondary"}>{a.estado || "N/A"}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </Card>
    </div>
  );
};

export default IntegracionPlanillasContable;
